import { CdkColumnDef } from '@angular/cdk/table';
import {
    AfterViewInit,
    Component,
    ContentChildren,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    QueryList,
    ViewChild
} from '@angular/core';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import {
    SortDirectionEnterprise,
    SortDirectionFinancing
} from '@lsq-frontend/angular-shared/api-service';
import { ClassNames } from '../../../constants/class-names.constants';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { Orientation } from '../../../constants/orientation.constants';
import { ColumnType } from '../../constants/column-type';
import { ITableColumn } from '../../models/table-column';
import { LsqDataTableComponent } from '../data-table/lsq-data-table.component';
import { CustomFooterCellDirective } from './custom-cells/custom-cell-footer.directive';
import { CustomHeaderCellDirective } from './custom-cells/custom-cell-header.directive';
import { CustomCellDirective } from './custom-cells/custom-cell.directive';

@Component({
    selector: 'lsq-column',
    template: `
        <ng-container [cdkColumnDef]="column.key">
            <th
                cdk-header-cell
                *cdkHeaderCellDef
                [ngStyle]="{
                    width: column.width || 'auto',
                    maxWidth: column.maxWidth || 'auto'
                }"
                [ngClass]="[
                    alignRight ? classNames.AlignRight : '',
                    column.truncateTextCss ? classNames.TextTruncate : '',
                    column.wrapText ? classNames.TextWrapTableCell : '',
                    column.sortable !== false ? classNames.Sortable : 'not-sortable',
                    !!column.headerPopoverContent ? classNames.ThWithPopover : '',
                    column.className || ''
                ]"
                (click)="changeSort()"
            >
                <!-- Custom Column Header -->
                <div *ngFor="let customHeader of customColumnHeaders">
                    <ng-container
                        *ngTemplateOutlet="customHeader.templateRef; context: { row: row }"
                    ></ng-container>
                </div>

                <!-- Default Column Header -->
                <ng-container *ngIf="!customColumnHeaders.length">
                    <div class="th-content">
                        <ng-container *ngIf="column.headerPopoverContent && !column.popoverIcon">
                            <popover-content
                                #headerPopover
                                [size]="column.headerPopoverSize || 'small'"
                            >
                                {{ column.headerPopoverContent }}
                            </popover-content>
                            <div
                                [popover]="headerPopover"
                                [popoverPlacement]="getHeaderPopoverPlacement()"
                            >
                                <span [innerHtml]="column.header"></span>
                            </div>
                        </ng-container>
                        <ng-container *ngIf="column.headerPopoverContent && column.popoverIcon">
                            <span [innerHtml]="column.header"></span>
                            <lsq-icon
                                [type]="icons.QuestionCircleLight"
                                [popoverOnHover]="true"
                                [height]="14"
                                [width]="14"
                                [style.marginLeft]="'1rem'"
                                [popover]="column.headerPopoverContent"
                            >
                            </lsq-icon>
                        </ng-container>

                        <ng-container *ngIf="!column.headerPopoverContent">
                            <span [innerHtml]="column.header"></span>
                        </ng-container>

                        <div *ngIf="isSortable$ | async">
                            <lsq-icon
                                class="sort-caret"
                                [ngClass]="{ 'is-sorted': isCurrentlySorted() }"
                                [type]="getIconType()"
                                [color]="getIconColor()"
                            >
                            </lsq-icon>
                        </div>
                    </div>
                </ng-container>
            </th>

            <td
                cdk-cell
                *cdkCellDef="let row; let index = index"
                [attr.colSpan]="colSpan"
                (click)="
                    column.onCellClick &&
                    (!column.isClickableFunction || column.isClickableFunction(row))
                        ? column.onCellClick(row)
                        : undefined
                "
                [ngStyle]="{
                    width: column.width || 'auto',
                    maxWidth: column.maxWidth || 'auto'
                }"
                [ngClass]="[
                    alignRight ? classNames.AlignRight : '',
                    column.wrapText ? classNames.TextWrapTableCell : '',
                    column.sortable !== false ? classNames.Sortable : 'not-sortable',
                    column.onCellClick &&
                    (!column.isClickableFunction || column.isClickableFunction(row))
                        ? classNames.CellClickable
                        : '',
                    column.className || ''
                ]"
            >
                <!-- Custom Column Cell Body -->
                <div *ngFor="let customCell of customColumnCells">
                    <ng-container
                        *ngTemplateOutlet="
                            customCell.templateRef;
                            context: { row: row, index: index }
                        "
                    ></ng-container>
                </div>

                <!-- Default Column Cell -->

                <ng-container *ngIf="!customColumnCells.length">
                    <span *ngIf="column.inlineLabel" class="inline-label text-uppercase">
                        {{ column.header }}
                    </span>

                    <!-- Render without truncating -->
                    <ng-container
                        *ngIf="
                            !column.truncateTextCss &&
                                !column.reverseTruncate &&
                                !column.truncateAfter;
                            else truncateTemplate
                        "
                    >
                        {{ row[column.key] ? row[column.key] : nullDataDash ? '—' : null }}
                    </ng-container>
                    <!-- Render with truncating -->
                    <ng-template #truncateTemplate>
                        <!-- Render with truncating without truncateAfter -->
                        <lsq-smart-truncate
                            *ngIf="
                                column.truncateTextCss && !column.truncateAfter;
                                else truncateAfterTemplate
                            "
                            [value]="row[column.key] ? row[column.key] : nullDataDash ? '—' : null"
                        ></lsq-smart-truncate>
                        <!-- Render with truncateAfter -->
                        <ng-template #truncateAfterTemplate>
                            <span
                                [popover]="row[column.key]"
                                [popoverDisabled]="row[column.key]?.length <= column.truncateAfter"
                            >
                                {{
                                    (row[column.key] ? row[column.key] : nullDataDash ? '—' : null)
                                        | truncateText: column.truncateAfter:column.reverseTruncate
                                }}
                            </span>
                        </ng-template>
                    </ng-template>

                    <span
                        *ngIf="column.inlineLabelSuffix"
                        class="inline-label text-uppercase suffix-label"
                    >
                        {{ column.header }}
                    </span>
                </ng-container>
            </td>

            <td
                cdk-footer-cell
                *cdkFooterCellDef
                [ngStyle]="{ width: column.width || 'auto' }"
                [ngClass]="[
                    alignRight ? classNames.AlignRight : '',
                    column.sortable !== false ? classNames.Sortable : 'not-sortable',
                    column.className || ''
                ]"
            >
                <!-- Render empty footer cell if no custom is provided. -->

                <!-- Custom Column Cell Footer -->
                <div *ngFor="let customFooter of customColumnFooters">
                    <ng-container
                        *ngTemplateOutlet="customFooter.templateRef; context: { row: row }"
                    ></ng-container>
                </div>
            </td>
        </ng-container>
    `
})
export class LsqBaseColumnComponent implements OnDestroy, AfterViewInit, OnInit {
    // These allow you to define custom header and cell templates (optional).
    @ContentChildren(CustomCellDirective) public customColumnCells: QueryList<CustomCellDirective>;
    @ContentChildren(CustomHeaderCellDirective) public customColumnHeaders: QueryList<
        CustomHeaderCellDirective
    >;
    @ContentChildren(CustomFooterCellDirective) public customColumnFooters: QueryList<
        CustomFooterCellDirective
    >;
    @ViewChild(CdkColumnDef) public columnDef: CdkColumnDef;
    @Input() public column: ITableColumn = {};
    @Input() public alignRight = false;
    @Input() public nullDataDash = false;
    @Input() public colSpan = 1;

    // Constants.
    public columnType = ColumnType;
    public classNames = ClassNames;
    public icons = Icons;

    public isSortable$: Observable<boolean>;

    constructor(@Optional() public dataTable: LsqDataTableComponent) {}

    public ngOnInit(): void {
        this.isSortable$ = this.dataTable.dataSource.noData$.pipe(
            map(
                (hasNoData) =>
                    !hasNoData &&
                    this.column.sortable !== false &&
                    this.column.type !== ColumnType.Action
            )
        );
    }

    public ngAfterViewInit(): void {
        if (this.dataTable) {
            this.dataTable.addColumn(this.columnDef);
        }
    }

    public ngOnDestroy() {
        if (this.dataTable.table) {
            this.dataTable.table.removeColumnDef(this.columnDef);
        }
    }

    public changeSort(): void {
        this.isSortable$.pipe(first()).subscribe((isSortable) => {
            if (isSortable) {
                const sortDirection = this.isCurrentlySorted()
                    ? this.getOppositeSortDirection()
                    : SortDirectionEnterprise.Desc;

                this.dataTable.dataSource.handleSort({
                    sortColumn: this.column.key,
                    sortDirection
                });
            }
        });
    }

    public getIconType(): string {
        if (this.isCurrentlySorted()) {
            return this.dataTable.dataSource.sortDirection === SortDirectionEnterprise.Desc ||
                this.dataTable.dataSource.sortDirection === SortDirectionFinancing.DESCENDING
                ? Icons.ChevronDown
                : Icons.ChevronUp;
        } else {
            return Icons.ChevronDown;
        }
    }

    public getIconColor(): Color {
        return this.isCurrentlySorted() ? Color.Black : Color.Gray;
    }

    public isCurrentlySorted(): boolean {
        if (this.dataTable.dataSource.sortColumn) {
            return this.dataTable.dataSource.sortColumn === this.column.key;
        } else {
            return false;
        }
    }

    private getOppositeSortDirection(): SortDirectionEnterprise {
        switch (this.dataTable.dataSource.sortDirection) {
            case SortDirectionEnterprise.Desc:
            case SortDirectionFinancing.DESCENDING:
                return SortDirectionEnterprise.Asc;
            case SortDirectionEnterprise.Asc:
            case SortDirectionFinancing.ASCENDING:
                return SortDirectionEnterprise.Desc;
            default:
                return 0;
        }
    }

    public getHeaderPopoverPlacement(): Orientation {
        return this.dataTable.headerIsStuck
            ? Orientation.Bottom
            : this.column.headerPopoverOrientation || Orientation.Top;
    }
}
